import {v3, v2, quat} from "oc-threejs/short3.js";
import Maths3 from "oc-threejs/Maths3.js";
import Textures from "oc-threejs/Textures.js";


/**
 * Wichtig für die Definition eines Rulers ist die Normale. Die Normale definiert zusammen mit der Messstrecke
 * die Ebene, in der der Ruler liegt. Aus der Normale und der Messstrecke wird die Offsetdirektion berechnet.
 *
 * Offset ist der Versatz des angezeigten Ruler von der Messstrecke.
 *
 * Es gibt 3 Offsetwerte, die den Versatz der vorderen Kante, der Mittellinie und der hinteren Kante
 * des Rulers bezeichnen.
 *
 *       x----------------------x
 *    Point 1                Point 2
 *       |
 *       | Offsetdirektion (senkrecht zur normal)
 *      \/
 *       x                      x  offset = [ offset1 x offsetdirection,
 *       |                      |
 *       x----------------------x             offset2 x offsetdirection,
 *       |                      |
 *       x                      x             offset3  x offsetdirection ]
 *
 */

export default class LengthRulerRepresentation extends THREE.Object3D {

    hideForSAOPASS=true;

    constructor(options) {
        super();

        this.setMeasureValues(options);
        this.name = "LengthRuler";

        this.textPlane = Textures.createTextSpriteOrPlane(this.getText(options),{
            orginalHeight: options.fontHeight,
            originalWidth: options.originalWidth,
            color: options.fontColor,
            backgroundColor: options.backgroundColor ? options.backgroundColor : "rgba(255,255,255,0)",
            textAsSprite: options.textAsSprite,
            depthTest:options.fontDepthTest,
            moveTextUpinHeightMultiplies: options.moveTextUpinHeightMultiplies,
            textCanvasCallback: options.textCanvasCallback,
            pixelPerUnit: options.pixelPerUnit,
        });
        this.setTextPlanePosition(options);
        this.add(this.textPlane);

        if (options.showLines) {
            this.showLinesIfLengthGreaterThen= options.showLinesIfLengthGreaterThen!=null?options.showLinesIfLengthGreaterThen:2;
            this.lineGeometry = new THREE.Geometry();
            this.setLineGeometryVertices(options);
            this.lineGeometry.dynamic = true;
            const line = new THREE.LineSegments( this.lineGeometry, options.createMeshMaterial(options.lineColor));
            this.add(line);
        }

        if (options.onTap) {
            this.tapable = true;
            this.onTapCallback = options.onTap;
        }

    }

    onTap() {
        if (this.onTapCallback) {
            this.onTapCallback();
        }
    }

    setMeasureValues(options) {
        this.lineVector = options.point2.clone().sub(options.point1);
        this.lengthDirection = this.lineVector.clone().normalize();
        this.length = this.lineVector.length();
        if (options.negative) {
            this.lengthDirection.negate();
            this.length *= -1;
        }
        this.offsetDirection = this.lengthDirection.clone().cross(options.normal.clone().negate());
        this.middlePoint = options.point1.clone().add(options.point2).multiplyScalar(0.5);
        this.textOffset = options.textOffset.clone();
    }

    offset(options,point,offsetIndex) {
        return point.clone().add(this.offsetDirection.clone().multiplyScalar(options.offsets[offsetIndex]));
    }

    getText(options) {
        return options.sizeFormatter(this.length);
    }

    setLineGeometryVertices(options) {
        const textSize = Math.min(this.length,this.showLinesIfLengthGreaterThen);
        const textVector = this.lengthDirection.clone().multiplyScalar(textSize/2);
        const middle1 = this.middlePoint.clone().sub(textVector);
        const middle2 = this.middlePoint.clone().add(textVector);

        const v = this.lineGeometry.vertices;
        v.length = 0;

        v.push(this.offset(options,options.point1, 1));
        v.push(this.offset(options,options.point1, 2));


        v.push(this.offset(options,options.point1, 0));
        v.push(this.offset(options,middle1, 0));

        v.push(this.offset(options,middle2, 0));
        v.push(this.offset(options,options.point2, 0));

        v.push(this.offset(options,options.point2, 1));
        v.push(this.offset(options,options.point2, 2));
    }

    setTextPlanePosition(options) {
        const angleClockwise = Maths3.angleClockwise(Maths3.vector.x,this.lengthDirection,options.normal) + Math.PI;
        const textRotation = options.textQuaternion ? options.textQuaternion :
            Maths3.quaternion.x270.clone().multiply(quat().setFromAxisAngle(v3(0,0,1),angleClockwise));

        this.textPlane.position.copy(this.offset(options,this.middlePoint,0).add(this.textOffset));
        this.textPlane.quaternion.copy(textRotation);

    }

    update(options) {
        this.setMeasureValues(options);
        this.setTextPlanePosition(options);
        this.textPlane.options.color = options.fontColor;
        this.textPlane.setText(this.getText(options));
        if (this.lineGeometry) {
            this.setLineGeometryVertices(options);
            this.lineGeometry.computeBoundingSphere();
            this.lineGeometry.verticesNeedUpdate = true;
            this.lineGeometry.lineDistancesNeedUpdate = true;
        }
    }

}

